import React, { useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Handshake, ExternalLink } from 'lucide-react';

const PartnersPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const partners = [
    {
      name: 'Musik i Gentofte',
      description: 'Moderne musikfestival i Gentofte Sportspark med danske topnavne.',
      logo: '/mig-logo.png',
      logoHeight: 'h-14',
      logoRounded: true,
      website: 'https://www.musikigentofte.dk',
    },
    {
      name: 'Festicon',
      description: 'Festivalmesse i Bella Center der samler festivaler, leverandører og arrangører.',
      logo: '/festicon-logo.png',
      logoHeight: 'h-8',
      needsDark: true,
      website: 'https://www.festicon.dk',
    },
    {
      name: 'S5',
      description: 'Leverandør af professionelle betalingsterminaler til event-industrien.',
      logo: '/s5-logo.avif',
      logoHeight: 'h-12',
      website: 'https://s5.dk',
    },
    {
      name: 'PAX Technology',
      description: 'Producent af POS-terminaler med høj pålidelighed.',
      logo: '/pax-logo.png',
      logoHeight: 'h-10',
      website: 'https://www.paxtechnology.com',
    },
    {
      name: 'EIFO',
      description: 'Danmarks Eksport- og Investeringsfond, som understøtter innovation og vækst i danske virksomheder.',
      logo: '/EIFO.jpg.webp',
      logoHeight: 'h-12',
      website: 'https://eifo.dk',
    },
    {
      name: 'Folkets Hus',
      description: 'Kultur- og mødested i Struer med fokus på fællesskab, oplevelser og lokale initiativer.',
      logo: '/folketshus.png',
      logoHeight: 'h-12',
      website: 'https://folketshus.struer.dk',
    },
    {
      name: 'SoundHub',
      description: 'Innovationsmiljø for lydteknologi, iværksætteri og udvikling af nye audio-løsninger.',
      logo: '/soundhub.png',
      logoHeight: 'h-12',
      website: 'https://soundhub.dk',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading font-bold text-foreground mb-5 text-4xl sm:text-5xl lg:text-6xl">
              Partnerskaber
            </h1>
            <p className="text-lg md:text-xl text-body-text leading-relaxed">
              MUSEIQ arbejder sammen med både eventarrangører og teknologipartnere for at skabe mere effektiv drift og bedre gæsteoplevelser.
            </p>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-20 md:py-28 bg-section-bg">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {partners.map((partner, index) => (
              <Card key={index} data-testid={`partner-${index}`} className="border-transparent hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(11,31,59,0.12)]">
                <CardContent className="p-8">
                  <div className="mb-6 min-h-16 flex items-center">
                    {partner.needsDark ? (
                      <div className="bg-primary/90 rounded-lg px-4 py-2 inline-block">
                        <img src={partner.logo} alt={partner.name} className={`${partner.logoHeight} w-auto`} />
                      </div>
                    ) : (
                      <img src={partner.logo} alt={partner.name} className={`${partner.logoHeight} w-auto ${partner.logoRounded ? 'rounded-lg' : ''}`} />
                    )}
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                    {partner.name}
                  </h3>
                  <p className="text-body-text mb-4">{partner.description}</p>
                  {partner.website && (
                    <a
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-accent-purple hover:underline"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      {partner.website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section-surface py-20 md:py-24">
        <div className="container-custom relative z-10">
          <div className="max-w-2xl mx-auto text-center text-white">
            <div className="w-14 h-14 rounded-full bg-gradient-brand flex items-center justify-center mx-auto mb-6">
              <Handshake className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              Bliv partner med MUSEIQ
            </h2>
            <p className="text-white/85 mb-8">
              Vil du samarbejde om at forbedre eventdrift og gæsteoplevelser? Vi er åbne for nye event- og teknologipartnerskaber.
            </p>
            <a href="mailto:info@museiq.io">
              <Button
                data-testid="partners-cta"
                size="lg"
                className="bg-white text-primary hover:bg-white/90 shadow-xl"
              >
                info@museiq.io
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnersPage;
